/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from "react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import GenericSwitch from "@ui-components/Switch/GenericSwitch";
import { GenericTable, GenericTableColumn } from "@ui-components/Table/GenericTable";
import { SkeletonText, SkeletonBlock } from "@ui-components/Skeleton/GenericSkeleton";
import { ReactComponent as IconEdit } from "../../../../../../shared/assets/Icons/IconEdit_KE.svg";
import { ReactComponent as IconDelete } from "../../../../../../shared/assets/Icons/Icon_Delete.svg";
import { ReactComponent as IconCheck } from "../../../../../../shared/assets/Icons/IconCheckSquared.svg";
import { ReactComponent as IconCancel } from "../../../../../../shared/assets/Icons/IconCrossSquare.svg";
import { Port, KEPort } from "../../request/request";

export interface PortEditModalProps {
  isOpen: boolean;
  isLoading: boolean;
  port: Port | null;
  onClose: () => void;
  onSave: (portData: Partial<Port>) => Promise<void>;
}

export const PortEditModal: React.FC<PortEditModalProps> = ({
  isOpen,
  isLoading,
  port,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState<Partial<Port>>({
    portName: "",
    id: "",
    portNumber: "",
    portType: "",
    portStatus: "available",
    isAvailable: false,
    decommissioned: false,
    isShared: false,
    hardwareResourceId: undefined,
    clientAssignation: "",
  });
  const [isSaving, setIsSaving] = useState(false);
  
  // Tab state
  const [activeTab, setActiveTab] = useState<"basic" | "kePorts">("basic");
  
  // KE Ports state for editing
  const [kePorts, setKePorts] = useState<KEPort[]>([]);
  const [editingKePortId, setEditingKePortId] = useState<number | null>(null);
  const [editingKePortData, setEditingKePortData] = useState<KEPort | null>(null);
  const [nextTempId, setNextTempId] = useState<number>(-1);

  useEffect(() => {
    if (port) {
      setFormData({
        portName: port.portName || "",
        id: port.id || "",
        portNumber: port.portNumber || "",
        portType: port.portType || "",
        portStatus: port.portStatus || "available",
        isAvailable: port.isAvailable ?? false,
        decommissioned: port.decommissioned ?? false,
        isShared: port.isShared ?? false,
        hardwareResourceId: port.hardwareResourceId,
        clientAssignation: port.clientAssignation || "",
        KEPorts: port.KEPorts || [],
      });
      // Initialize KE Ports state
      setKePorts(port.KEPorts || []);
      // Reset to basic tab when modal opens
      setActiveTab("basic");
      // Reset editing state
      setEditingKePortId(null);
      setEditingKePortData(null);
      setNextTempId(-1);
    }
  }, [port, isOpen]);

  const handleInputChange = (field: keyof Port, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleBooleanChange = (field: keyof Port, value: boolean) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      
      // Prepare payload according to API format
      const payload: any = {
        hardwareResourceId: formData.hardwareResourceId ? Number(formData.hardwareResourceId) : undefined,
        portDescription: formData.portName || "",
        portNumber: formData.portNumber || "",
        portType: formData.portType || "",
        portStatus: formData.portStatus || "available",
        clientAssignation: formData.clientAssignation || "",
        isAvailable: formData.isAvailable ?? false,
        decommissioned: formData.decommissioned ?? false,
        isShared: formData.isShared ?? false,
      };
      
      await onSave(payload);
      onClose();
    } catch (error) {
      console.error("Error saving port:", error);
    } finally {
      setIsSaving(false);
    }
  };
  
  // Check if KE Ports tab should be enabled (always enabled if we have port data, even if no KE Ports yet)
  const hasKEPorts = !!port; // Always show tab if port exists
  
  const TABS = [
    { id: "basic", label: "Basic Information" },
    { id: "kePorts", label: "KE Ports Information" },
  ];
  
  // KE Ports handlers
  const handleEditKePort = (kePortId: number) => {
    const kePort = kePorts.find(kp => kp.id === kePortId);
    if (kePort) {
      setEditingKePortId(kePortId);
      setEditingKePortData({ ...kePort });
    }
  };
  
  const handleSaveKePort = (kePortId: number) => {
    if (editingKePortData) {
      // Update the row in kePorts array
      const updatedKePorts = kePorts.map(kp => 
        kp.id === kePortId ? editingKePortData : kp
      );
      
      setKePorts(updatedKePorts);
      setEditingKePortId(null);
      setEditingKePortData(null);
      
      // Update formData with new KE Ports
      setFormData(prev => ({
        ...prev,
        KEPorts: updatedKePorts,
      }));
    }
  };
  
  const handleCancelKePort = () => {
    // If canceling a new row (negative ID), remove it
    if (editingKePortId !== null && editingKePortId < 0) {
      const updatedKePorts = kePorts.filter(kp => kp.id !== editingKePortId);
      setKePorts(updatedKePorts);
      setFormData(prev => ({
        ...prev,
        KEPorts: updatedKePorts,
      }));
    }
    setEditingKePortId(null);
    setEditingKePortData(null);
  };
  
  const handleDeleteKePort = (kePortId: number) => {
    const updatedKePorts = kePorts.filter(kp => kp.id !== kePortId);
    setKePorts(updatedKePorts);
    
    // If we're deleting the row that's being edited, reset editing state
    if (editingKePortId === kePortId) {
      setEditingKePortId(null);
      setEditingKePortData(null);
    }
    
    // Update formData with new KE Ports
    setFormData(prev => ({
      ...prev,
      KEPorts: updatedKePorts,
    }));
  };
  
  const handleAddKePort = () => {
    // Only allow adding if no row is currently being edited
    if (editingKePortId !== null) {
      return;
    }
    
    const newKePort: KEPort = {
      id: nextTempId,
      subscriptionId: "",
      tenantId: 0,
      name: "",
      capacity: 0,
    };
    
    // Add new row at the beginning
    setKePorts([newKePort, ...kePorts]);
    setEditingKePortId(nextTempId);
    setEditingKePortData({ ...newKePort });
    setNextTempId(prev => prev - 1);
    
    // Update formData
    setFormData(prev => ({
      ...prev,
      KEPorts: [newKePort, ...kePorts],
    }));
  };
  
  const handleEditingKePortChange = (field: keyof KEPort, value: string | number) => {
    if (editingKePortData) {
      setEditingKePortData({
        ...editingKePortData,
        [field]: value,
      });
    }
  };
  
  // KE Ports table columns
  const kePortsColumns: GenericTableColumn<KEPort>[] = [
    {
      header: "Subscription ID",
      accessor: "subscriptionId",
      renderCell: (row) => {
        if (editingKePortId === row.id) {
          return (
            <GenericInput
              label=""
              value={editingKePortData?.subscriptionId || ""}
              onChange={(value) => handleEditingKePortChange("subscriptionId", value)}
              placeholder="Subscription ID"
            />
          );
        }
        return <span className="text-sm text-semantic-text-secondary">{row.subscriptionId || "-"}</span>;
      },
    },
    {
      header: "Tenant ID",
      accessor: "tenantId",
      renderCell: (row) => {
        if (editingKePortId === row.id) {
          return (
            <GenericInput
              label=""
              value={editingKePortData?.tenantId ? String(editingKePortData.tenantId) : ""}
              onChange={(value) => handleEditingKePortChange("tenantId", Number(value) || 0)}
              type="number"
              placeholder="Tenant ID"
            />
          );
        }
        return <span className="text-sm text-semantic-text-secondary">{row.tenantId || "-"}</span>;
      },
    },
    {
      header: "Name",
      accessor: "name",
      renderCell: (row) => {
        if (editingKePortId === row.id) {
          return (
            <GenericInput
              label=""
              value={editingKePortData?.name || ""}
              onChange={(value) => handleEditingKePortChange("name", value)}
              placeholder="Name"
            />
          );
        }
        return <span className="text-sm text-semantic-text-secondary">{row.name || "-"}</span>;
      },
    },
    {
      header: "Capacity",
      accessor: "capacity",
      renderCell: (row) => {
        if (editingKePortId === row.id) {
          return (
            <GenericInput
              label=""
              value={editingKePortData?.capacity ? String(editingKePortData.capacity) : ""}
              onChange={(value) => handleEditingKePortChange("capacity", Number(value) || 0)}
              type="number"
              placeholder="Capacity"
            />
          );
        }
        return <span className="text-sm text-semantic-text-secondary">{row.capacity || "-"}</span>;
      },
    },
    {
      header: "Actions",
      accessor: "id",
      renderCell: (row) => {
        if (editingKePortId === row.id) {
          return (
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleSaveKePort(row.id)}
                className="flex items-center justify-center border border-semantic-border-secondary rounded text-semantic-text-secondary hover:bg-semantic-state-active w-8 h-8"
                title="Save"
              >
                <IconCheck className="w-5 h-5" />
              </button>
              <button
                onClick={handleCancelKePort}
                className="flex items-center justify-center border border-semantic-border-secondary rounded text-semantic-text-secondary hover:bg-semantic-state-active w-8 h-8"
                title="Cancel"
              >
                <IconCancel className="w-5 h-5" />
              </button>
            </div>
          );
        }
        return (
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleEditKePort(row.id)}
              className="flex items-center justify-center border border-semantic-border-secondary rounded text-semantic-text-secondary hover:bg-semantic-state-active w-8 h-8"
              title="Edit"
            >
              <IconEdit className="w-5 h-5" />
            </button>
            <button
              onClick={() => handleDeleteKePort(row.id)}
              className="flex items-center justify-center border border-semantic-border-secondary rounded text-semantic-text-secondary hover:bg-red-50 hover:border-red-300 hover:text-red-600 w-8 h-8"
              title="Delete"
            >
              <IconDelete className="w-5 h-5" />
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <GenericModal 
      isOpen={isOpen} 
      onClose={onClose} 
      title="Edit Port"
      customWidth="max-w-7xl"
    >
      {isLoading ? (
        <div className="space-y-6">
          <div>
            <SkeletonBlock h={20} w="30%" className="mb-3" />
            <div className="grid grid-cols-2 gap-4">
              {Array.from({ length: 5 }).map((_, idx) => (
                <SkeletonText key={idx} lines={2} lineHeight={14} />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-6 min-h-[600px]">
          {/* Tabs */}
          <div className="border-b border-gray-200 mb-4">
            <nav className="flex space-x-4" aria-label="Tabs">
              {TABS.map((tab) => {
                const disabled = tab.id === "kePorts" && !hasKEPorts;
                return (
                  <button
                    key={tab.id}
                    onClick={() => !disabled && setActiveTab(tab.id as "basic" | "kePorts")}
                    disabled={disabled}
                    className={`
                      px-3 py-2 text-sm font-medium focus:outline-none rounded-t
                      ${
                        activeTab === tab.id
                          ? "text-semantic-text-brand border-b-2 border-semantic-border-brand-primary bg-semantic-bg-secondary"
                          : disabled
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-gray-500 hover:text-gray-700"
                      }
                    `}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
          
          {/* Basic Information Tab */}
          {activeTab === "basic" && (
            <GenericCard isCollapsible={false} className="p-5">
              <div className="grid grid-cols-2 gap-4">
                <GenericInput
                  label="Port Description"
                  value={formData.portName || ""}
                  onChange={(value) => handleInputChange("portName", value)}
                />
                <GenericInput
                  label="Hardware Resource ID"
                  value={formData.hardwareResourceId ? String(formData.hardwareResourceId) : ""}
                  onChange={(value) => {
                    const numValue = value ? Number(value) : undefined;
                    setFormData((prev) => ({
                      ...prev,
                      hardwareResourceId: numValue,
                    }));
                  }}
                  type="number"
                />
                <GenericInput
                  label="Port Number"
                  value={formData.portNumber || ""}
                  onChange={(value) => handleInputChange("portNumber", value)}
                />
                <GenericInput
                  label="Port Type"
                  value={formData.portType || ""}
                  onChange={(value) => handleInputChange("portType", value)}
                />
                <div className="flex flex-col">
                  <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                    Port Status
                  </label>
                  <GenericDropdown
                    items={["im-state-admin-down", "im-state-up", "decommissioned", "im-state-down", "available"]}
                    value={formData.portStatus || "available"}
                    defaultLabel="Select Port Status"
                    onSelect={(value) => handleInputChange("portStatus", value)}
                    minWidthClass="min-w-[200px]"
                    maxWidthClass="max-w-[240px]"
                  />
                </div>
                <GenericInput
                  label="Client Assignation"
                  value={formData.clientAssignation || ""}
                  onChange={(value) => handleInputChange("clientAssignation", value)}
                />
                {/* Boolean fields as switches */}
                <div className="flex flex-col">
                  <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                    Available
                  </label>
                  <div className="flex items-center gap-2">
                    <GenericSwitch
                      checked={formData.isAvailable ?? false}
                      onChange={(value) => handleBooleanChange("isAvailable", value)}
                    />
                    <span className="text-sm text-semantic-text-secondary">
                      {formData.isAvailable ? "Yes" : "No"}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col">
                  <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                    Decommissioned
                  </label>
                  <div className="flex items-center gap-2">
                    <GenericSwitch
                      checked={formData.decommissioned ?? false}
                      onChange={(value) => handleBooleanChange("decommissioned", value)}
                    />
                    <span className="text-sm text-semantic-text-secondary">
                      {formData.decommissioned ? "Yes" : "No"}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col">
                  <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                    Shared
                  </label>
                  <div className="flex items-center gap-2">
                    <GenericSwitch
                      checked={formData.isShared ?? false}
                      onChange={(value) => handleBooleanChange("isShared", value)}
                    />
                    <span className="text-sm text-semantic-text-secondary">
                      {formData.isShared ? "Yes" : "No"}
                    </span>
                  </div>
                </div>
              </div>
            </GenericCard>
          )}
          
          {/* KE Ports Information Tab */}
          {activeTab === "kePorts" && (
            <GenericCard isCollapsible={false} className="p-5">
              <div className="flex justify-between mb-10">
                <GenericInput
                  label="KE Port Count"
                  value={String(kePorts.length)}
                  readOnly
                />
                <div className="flex items-end">
                  <GenericButton
                    label="Add Port"
                    variant="brand"
                    onClick={handleAddKePort}
                    disabled={editingKePortId !== null}
                  />
                </div>
              </div>
                <div className="mt-8">
                    <GenericTable
                    columns={kePortsColumns}
                    data={kePorts}
                    width="100%"
                    maxTableBodyHeight="400px"
                    isFooterVisible={false}
                    />
              </div>
            </GenericCard>
          )}
        </div>
      )}

      <div className="flex justify-end gap-2 mt-6">
        <GenericButton 
          label="Cancel" 
          variant="secondary" 
          onClick={onClose}
          disabled={isSaving}
        />
        <GenericButton 
          label={isSaving ? "Saving..." : "Save"} 
          variant="brand" 
          onClick={handleSave}
          disabled={isSaving || isLoading}
        />
      </div>
    </GenericModal>
  );
};
